import type { DynamicPageRoute, StaticPageRoute } from "@/gen/routeTree.gen";

type ParamsFor<P extends string> = {
  [K in ExtractParams<P>]: ParamValue;
};
export type RouteLiteral = DynamicRoutePath | StaticRoutePath;

// ------------- Distributive RouteObject -------------
// Builds a union of correctly-typed objects for every RouteLiteral.
// This lets you write `RouteObject` with NO generic argument.
export type RouteObjectFor<P extends RouteLiteral> = ([ExtractParams<P>] extends [never]
  ? { params?: never }
  : { params: ParamsFor<P> }) & {
  hash?: string;
  searchParams?: SearchParams;
} & { path: P };

// Given a route literal, produce the correct Next.js param shape.
// - [id]            -> { id: string }
// - [...slug]       -> { slug: string[] }
// - [[...rest]]     -> { rest?: string[] }  // optional catch-all
export type RouteParams<P extends RouteLiteral> = {
  [K in ExtractParams<P>]: P extends `${string}[[...${K}]]${string}`
    ? string[] | undefined
    : P extends `${string}[...${K}]${string}`
      ? string[]
      : string;
};
type SearchParams = Record<string, ListParam | PrimitiveParam | undefined> | URLSearchParams;

type DynamicRoutePath = DynamicPageRoute["path"];

// ------------- Param extraction utilities -------------
type ExtractParams<S extends string> = S extends `${string}[[...${infer P}]]${infer R}`
  ? ExtractParams<R> | P
  : S extends `${string}[...${infer P}]${infer R}`
    ? ExtractParams<R> | P
    : S extends `${string}[${infer P}]${infer R}`
      ? ExtractParams<R> | P
      : never;

type ListParam = PrimitiveParam[];

type ParamValue = ListParam | PrimitiveParam | undefined;

type PrimitiveParam = boolean | number | string;

// ---------------- Route literals ----------------
type StaticRoutePath = StaticPageRoute;

export function route<P extends RouteLiteral>(routeObject: RouteObjectFor<P>): string {
  const base = buildPathFromTemplate(
    routeObject.path as string,
    (routeObject as { params?: Record<string, ParamValue> }).params ?? {},
  );
  const qs = buildQueryString(routeObject.searchParams);
  const h = routeObject.hash ? `#${stripHash(routeObject.hash)}` : "";
  return `${base}${qs}${h}`;
}

// ================= Helpers (named functions) =================
function buildPathFromTemplate(template: string, params: Record<string, ParamValue>): string {
  const rawParts = template.split("/");
  const outParts: string[] = [];

  for (let i = 0; i < rawParts.length; i++) {
    const seg = rawParts[i];
    if (!seg) continue;

    if (isOptionalCatchAll(seg)) {
      const name = extractOptionalCatchAllName(seg);
      const value = params[name];
      if (value === undefined || (Array.isArray(value) && value.length === 0)) continue;
      const expanded = Array.isArray(value) ? value : [value];
      for (let j = 0; j < expanded.length; j++) outParts.push(encodeSegment(expanded[j]));
      continue;
    }

    if (isCatchAll(seg)) {
      const name = extractCatchAllName(seg);
      const value = params[name];
      if (value === undefined) throw new Error(`Missing value for catch-all param "${name}" in "${template}"`);
      const expanded = Array.isArray(value) ? value : [value];
      if (expanded.length === 0) throw new Error(`Catch-all param "${name}" must not be empty in "${template}"`);
      for (let j = 0; j < expanded.length; j++) outParts.push(encodeSegment(expanded[j]));
      continue;
    }

    if (isSingleParam(seg)) {
      const name = extractSingleName(seg);
      const value = params[name];
      if (value === undefined || Array.isArray(value)) {
        throw new Error(`Missing or invalid value for param "${name}" in "${template}"`);
      }
      outParts.push(encodeSegment(value));
      continue;
    }

    outParts.push(seg);
  }

  const joined = "/" + outParts.join("/");
  return joined !== "/" && joined.endsWith("/") ? joined.slice(0, -1) : joined;
}
function buildQueryString(searchParams?: SearchParams): string {
  if (!searchParams) return "";
  if (searchParams instanceof URLSearchParams) {
    const s = searchParams.toString();
    return s ? `?${s}` : "";
  }
  const usp = new URLSearchParams();
  const keys = Object.keys(searchParams);
  for (let i = 0; i < keys.length; i++) {
    const key = keys[i];
    const val = searchParams[key];
    if (val === undefined) continue;
    if (Array.isArray(val)) {
      for (let j = 0; j < val.length; j++) usp.append(key, String(val[j]));
    } else {
      usp.set(key, String(val));
    }
  }
  const str = usp.toString();
  return str ? `?${str}` : "";
}

function encodeSegment(value: PrimitiveParam): string {
  return encodeURIComponent(String(value));
}

function extractCatchAllName(seg: string): string {
  return seg.slice(4, -1);
}

function extractOptionalCatchAllName(seg: string): string {
  return seg.slice(4, -2);
}

function extractSingleName(seg: string): string {
  return seg.slice(1, -1);
}

function isCatchAll(seg: string): boolean {
  return /^\[\.\.\.[^\]]+\]$/.test(seg);
}
function isOptionalCatchAll(seg: string): boolean {
  return /^\[\[\.\.\.[^\]]+\]\]$/.test(seg);
}

function isSingleParam(seg: string): boolean {
  return /^\[[^\].]+\]$/.test(seg);
}

function stripHash(hash: string): string {
  return hash.startsWith("#") ? hash.slice(1) : hash;
}
